<template>
  <Modal
    class="scoped"
    :modal-style="{ maxWidth: modalMaxWidth }"
    title="Import Query Snippets"
  >
    <div style="max-width: 100vw">
      <QCardSection class="row items-center">
        <h4>Import Query Snippets via CSV</h4>
      </QCardSection>

      <QCardSection v-if="step === 1">
        <div class="row">
          <QFile
            v-model="csvFile"
            accept=".csv"
            class="full-width"
            filled
            label="Upload csv"
            square
            @update:model-value="processCsvFile"
          />
        </div>
      </QCardSection>

      <QCardSection v-if="step === 1">
        <VueJsonCsv
          class="col"
          :data="sampleQuerySnippetsCsvData"
          name="sample-query-snippet.csv"
        >
          <QBtn
            color="primary"
            flat
            label="Sample Query Snippet.csv"
            no-caps
          />
        </VueJsonCsv>
      </QCardSection>

      <QCardSection v-if="step === 2">
        <div class="row">
          <QMarkupTable
            class="full-width scroll"
            style="height: 600px"
          >
            <thead>
              <tr>
                <th class="column-status text-left">Valid</th>
                <th class="col-label text-left">Label</th>
                <th class="column-label text-left">Group</th>
                <th class="column-query text-left">Query</th>
                <th class="column-query text-left">Filter Set</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(querySnippet, index) in querySnippetsToImport"
                :key="index"
                :class="{
                  'query-stream-error': querySnippetError(querySnippet),
                }"
              >
                <td class="column-status text-center">
                  <QSpinner v-if="querySnippet.loading" />
                  <QIcon
                    v-if="!querySnippet.loading"
                    size="sm"
                    v-bind="statusAttributes(querySnippet)"
                  />
                </td>
                <td class="column-label text-left">
                  <QInput
                    v-model="querySnippet.label"
                    filled
                    square
                  />
                </td>
                <td class="column-label text-left">
                  <QInput
                    v-model="querySnippet.group"
                    filled
                    square
                  />
                </td>
                <td class="column-query text-left">
                  <div class="q-pa-md">
                    <QBtn
                      dense
                      flat
                      :icon-right="
                        querySnippet.expanded
                          ? 'keyboard_arrow_up'
                          : 'keyboard_arrow_down'
                      "
                      label="Show query"
                      no-caps
                      @click="querySnippet.expanded = !querySnippet.expanded"
                    />
                  </div>
                  <div
                    v-if="querySnippet.expanded"
                    class="row full-width table-row"
                  >
                    <QueryEditor
                      v-model="querySnippet.boolean_query"
                      :brief="querySnippet"
                      class="full-width"
                      hide-controls
                      label=""
                      :media-types="types"
                      show-error
                      @has-error="updateError(querySnippet, $event)"
                    />
                  </div>
                  <p
                    v-if="!querySnippet.valid"
                    class="text-ds-red-5"
                  >
                    {{ querySnippet.validationMessage }}
                  </p>
                </td>
                <td class="column-label text-left">
                  <QInput
                    v-model="querySnippet.filter_set"
                    filled
                    square
                  />
                </td>
              </tr>
            </tbody>
          </QMarkupTable>
        </div>
      </QCardSection>

      <QCardActions>
        <div class="full-width flex justify-between">
          <div>
            <QBtn
              v-if="step === 2"
              color="primary"
              flat
              label="Back"
              no-caps
              @click="step = 1"
            />
          </div>
          <div>
            <QBtn
              v-close-popup
              color="primary"
              flat
              label="Cancel"
              no-caps
              @click="$emit('cancel')"
            />
            <QBtn
              v-if="step === 1"
              color="black"
              :disabled="disabled"
              :label="validateLabel"
              no-caps
              @click="validate"
            />
            <QBtn
              v-if="step === 2"
              v-close-popup
              color="black"
              :disabled="disabled"
              :label="importLabel"
              no-caps
              @click="confirm"
            />
          </div>
        </div>
      </QCardActions>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import Papa from "papaparse";
import { useQuasar } from "quasar";
import type { Ref } from "vue";
import { computed, ref } from "vue";
import VueJsonCsv from "vue-json-csv";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiAdmin } from "shared/boot/api";
import Modal from "shared/components/core/modals/Modal.vue";
import { QueryEditor } from "shared/components/QueryEditor";
import { getSources, getTypesForSources } from "shared/helpers/media";
import { pluralize } from "shared/helpers/string";

interface Props {
  organisationId: number | string;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

interface GroupLabel {
  label: string;
}

interface QuerySnippetCsvData {
  label: string;
  group: string;
  boolean_query: string;
  filter_set: string;
  [index: string]: string | number | boolean | GroupLabel;
}

interface QuerySnippetToImport extends QuerySnippetCsvData {
  organisation_id: string | number;
  valid: boolean;
  loading: boolean;
  validationMessage: string;
  expanded: boolean;
}

interface QuerySnippetToImportParam
  extends Omit<QuerySnippetToImport, "group"> {
  group: GroupLabel;
}

const querySnippetsToImport: Ref<QuerySnippetToImport[]> = ref([]);

const sampleQuerySnippetsCsvData: QuerySnippetCsvData[] = [
  {
    label: "String - Required",
    group: "String - Required",
    boolean_query: "String - Required",
    filter_set: "String - Optional",
  },
  {
    label: "My Stream",
    group: "My Group",
    boolean_query: "this OR that",
    filter_set: "Filter Set Label",
  },
];

const quasar = useQuasar();
const step: Ref<number> = ref(1);
const csvFile: Ref<File | null> = ref(null);

const modalMaxWidth = computed<string>(() =>
  step.value === 2 ? "100%" : "500px"
);

const disabled = computed<boolean>(
  () => !props.organisationId || !querySnippetsToImport.value.length
);

const pluralString = computed<string>(
  () =>
    `${querySnippetsToImport.value.length} ${pluralize(
      querySnippetsToImport.value.length,
      "Query Snippet"
    )}`
);

const validateLabel = computed<string>(() => `Validate ${pluralString.value}`);
const importLabel = computed<string>(() => `Import ${pluralString.value}`);

const media = computed<string[]>(() =>
  getSources().map(({ medium }: { medium: string }) => medium)
);

const types = computed<string[]>(() => getTypesForSources(getSources()));

function processCsvFile(): void {
  if (!csvFile.value) return;

  const reader: FileReader = new FileReader();

  reader.onload = (event: ProgressEvent<FileReader>) => {
    const csvFileContent: string = (event?.target?.result as string)?.trim();
    const parsed: string[][] = Papa.parse<string[]>(csvFileContent).data;
    const headers: string[] | undefined = parsed.shift();

    if (!headers) return;

    querySnippetsToImport.value = parsed.map((values: string[]) => {
      // set columns uploaded
      const csvData: QuerySnippetCsvData = values.reduce<QuerySnippetCsvData>(
        (acc: QuerySnippetCsvData, value: string, index: number) => {
          const column: string = headers[index];
          acc[column] = value;

          return acc;
        },
        {
          label: "",
          group: "",
          boolean_query: "",
          filter_set: "",
        }
      );

      const querySnippet: QuerySnippetToImport = {
        ...csvData,
        organisation_id: props.organisationId,
        valid: false,
        loading: false,
        validationMessage: "",
        expanded: false,
      };

      return querySnippet;
    });
  };

  reader.readAsText(csvFile.value);
}

async function validateQuery(
  querySnippet: QuerySnippetToImport
): Promise<void> {
  try {
    Object.assign<QuerySnippetToImport, Pick<QuerySnippetToImport, "loading">>(
      querySnippet,
      {
        loading: true,
      }
    );

    await ($streemApiAdmin as any).post("search", {
      params: {
        limit: 0,
        query: querySnippet.boolean_query,
        media: media.value,
        isQuerySnippet: true,
        organisationId: props.organisationId,
      },
      transformKeys: true,
    });

    Object.assign<QuerySnippetToImport, Pick<QuerySnippetToImport, "valid">>(
      querySnippet,
      {
        valid: true,
      }
    );
  } catch (error: any) {
    const message: string = error.response?.data?.error?.message
      ? error.response?.data?.error?.message
      : error.message;

    Object.assign<
      QuerySnippetToImport,
      Pick<QuerySnippetToImport, "valid" | "validationMessage">
    >(querySnippet, {
      valid: false,
      validationMessage: `Error: ${message.replace('"\\\\"', '""')}`,
    });
  } finally {
    Object.assign<QuerySnippetToImport, Pick<QuerySnippetToImport, "loading">>(
      querySnippet,
      {
        loading: false,
      }
    );
  }
}

function validate(): void {
  step.value = 2;
  querySnippetsToImport.value.forEach(validateQuery);
}

function statusAttributes(querySnippet: QuerySnippetToImport): {
  name: string;
  color: string;
} {
  const { valid } = querySnippet;

  return {
    name: valid ? "check" : "error",
    color: valid ? "green" : "red",
  };
}

function updateError(
  querySnippet: QuerySnippetToImport,
  hasError: boolean
): void {
  Object.assign<QuerySnippetToImport, Pick<QuerySnippetToImport, "valid">>(
    querySnippet,
    {
      valid: !hasError,
    }
  );
}

function querySnippetError(querySnippet: QuerySnippetToImport): boolean {
  return !querySnippet.valid;
}

function uploadQuerySnippets(): Promise<any> {
  const querySnippetParams: QuerySnippetToImportParam[] =
    querySnippetsToImport.value.map((querySnippet: QuerySnippetToImport) => {
      const params: QuerySnippetToImportParam = {
        ...querySnippet,
        group: {
          label: querySnippet.group,
        },
      };

      return params;
    });

  return ($streemApiAdmin as any).post("query_snippets/import", {
    transformKeys: true,
    params: { querySnippets: querySnippetParams },
  });
}

interface UploadResponse {
  id: number;
  error: string;
  label: string;
}

async function confirm(): Promise<void> {
  try {
    quasar.loading.show({ delay: 500 });
    const result: UploadResponse[] = (await uploadQuerySnippets()).data;

    const failedQuerySnippets: UploadResponse[] = result.filter(
      (querySnippetStatus: UploadResponse) => querySnippetStatus.error
    );

    const failedRows: string = failedQuerySnippets
      .map(
        (querySnippet) =>
          `<tr><td>${querySnippet.label}</td><td>${querySnippet.error}</td></tr>`
      )
      .join("\n");

    const successfulQuerySnippets = result.length - failedQuerySnippets.length;

    let message: string = `
          <strong>
            Sucessfully imported ${successfulQuerySnippets} of ${pluralString.value}
          </strong>
        `;

    if (failedQuerySnippets.length) {
      message += `
            <br/>
            <br/><strong>Failed Query Snippets:</strong><br/>
            <table class="q-table">
              <thead>
                <tr>
                  <td>Label</td>
                  <td>Fail Reason</td>
                </tr>
              </thead>
              <tbody>
                ${failedRows}
              </tbody>
            </table>
          `;
    }

    emit("confirm");
    emit("cancel");

    quasar.dialog({
      html: true,
      title: "Import Complete",
      message,
      persistent: true,
      fullWidth: Boolean(failedQuerySnippets.length),
      ok: "Done",
    });
  } catch (error: any) {
    ErrorDialog("There was an error importing the query snippets", error);
  } finally {
    quasar.loading.hide();
  }
}
</script>

<style lang="scss" scoped>
.column-status {
  width: 2%;
  min-width: 2%;
  max-width: 2%;
}

.column-label {
  width: 10%;
  min-width: 10%;
  max-width: 10%;
}

.column-query {
  width: 30%;
  min-width: 30%;
  max-width: 30%;
  padding: 5px;
}

.column-checkbox {
  width: 5%;
  min-width: 5%;
  max-width: 5%;
  padding: 0;
}

.query-stream-error {
  background-color: #ff78781f;
}
</style>
